/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { useMutation } from '@tanstack/react-query';

import { ApiError, UpdateUserSubtenantsData, UpdateUserSubtenantsResponse } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type UpdateUserSubtenantsMutationConfig = {
  onSuccess?: (data: void, variables: UpdateUserSubtenantsData) => void;
  onError?: (error: unknown, variables: UpdateUserSubtenantsData) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
  onMutate?: (variables: UpdateUserSubtenantsData) => Promise<unknown> | void;
};

/**
 * Hook for updating user subtenant roles by ID through the management plane API.
 *
 * @param config.onSuccess - Called after successful user subtenants update
 * @param config.onError - Called if update fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for updating user
 *
 * @example
 * ```tsx
 * const { mutate } = useUpdateUserSubtenantsMutation({
 *   onSuccess: (data) => console.log('User subtenants updated:', data),
 *   onError: (error) => console.error('User subtenants update failed:', error)
 * });
 *
 * // Update user subtenants
 * mutate({ id: '9fb763f7-33de-4969-a7a0-b808f4637318', subtenants: [{subtenantId: '748de1a3-ce77-9854-b8b2-f8f190234809', role: 'viewer }] });
 * ```
 */

export function useUpdateUserSubtenantsMutation({
  onSuccess,
  onError,
  onSettled,
  onMutate,
}: UpdateUserSubtenantsMutationConfig) {
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, isError, error } = useMutation<UpdateUserSubtenantsResponse, ApiError, UpdateUserSubtenantsData>({
    mutationFn: ({ id: userId, requestBody }) => {
      return mgmtPlaneAPIClient.users.updateUserSubtenants({
        tenantId: tenantId!,
        id: userId,
        requestBody,
      });
    },
    onSuccess,
    onError,
    onSettled,
    onMutate,
  });

  return { mutate, isError, error };
}
